import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Trash2, Edit, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { useComposer } from '@/stores/composer.store';
import { draftsApi } from '@/lib/api/endpoints';

export function DraftsPage() {
  const qc = useQueryClient();
  const openCompose = useComposer((s) => s.openCompose);

  const draftsQuery = useQuery({
    queryKey: ['drafts'],
    queryFn: () => draftsApi.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => draftsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts'] });
      toast.success('Bozza eliminata');
    },
  });

  const drafts = draftsQuery.data?.drafts ?? [];

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <div className="flex-1 flex flex-col">
        <div className="border-b px-6 py-4">
          <h1 className="text-xl font-semibold">Bozze</h1>
          <p className="text-sm text-muted-foreground">{drafts.length} {drafts.length === 1 ? 'bozza' : 'bozze'} salvate</p>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {drafts.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              <div className="text-4xl mb-3">✉️</div>
              <p>Nessuna bozza al momento.</p>
            </div>
          ) : (
            <div className="space-y-2 max-w-3xl">
              {drafts.map((d) => {
                const to = d.to_addresses?.[0]?.address ?? '(nessun destinatario)';
                const isScheduled = !!d.scheduled_send_at && !d.sent_at;
                const hasError = !!d.send_error;
                return (
                  <div
                    key={d.id}
                    className="border rounded-lg p-4 hover:bg-muted/30 transition-colors flex items-start gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-muted-foreground">A:</span>
                        <span className="text-sm font-medium truncate">{to}</span>
                        {isScheduled && (
                          <span className="text-xs flex items-center gap-1 text-blue-600">
                            <Clock className="h-3 w-3" />
                            Programmato {format(new Date(d.scheduled_send_at!), 'd MMM HH:mm', { locale: it })}
                          </span>
                        )}
                        {hasError && (
                          <span className="text-xs flex items-center gap-1 text-destructive">
                            <AlertCircle className="h-3 w-3" /> Errore invio
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-medium truncate">{d.subject || '(senza oggetto)'}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Aggiornata {format(new Date(d.updated_at), 'd MMM HH:mm', { locale: it })}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openCompose({ draftId: d.id })}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteMutation.mutate(d.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
